import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { appAnalyticsAPI } from '../../services/api';
import {
  AppFilters, AppNav, AppStyles, FilterBar, NoPlanNotice, StatCard, defaultFilters, filterParams, fmtDuration, fmtMoney, fmtNum,
} from './common';

const BreakdownList: React.FC<{ title: string; icon: string; rows: any[] }> = ({ title, icon, rows }) => {
  const max = Math.max(1, ...rows.map(r => r.users));
  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <h6 className="fw-bold mb-3"><i className={`bi ${icon} me-1`}></i>{title}</h6>
        {rows.length === 0 && <div className="text-muted small">No data yet</div>}
        {rows.map(r => (
          <div key={r.label} className="mb-2">
            <div className="d-flex justify-content-between small"><span className="text-truncate me-2">{r.label}</span><span className="text-muted">{fmtNum(r.users)} users · {fmtNum(r.events)}</span></div>
            <div className="progress" style={{ height: 6 }}><div className="progress-bar" style={{ width: `${(r.users / max) * 100}%`, background: '#8b5cf6' }}></div></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AppOverviewPage: React.FC = () => {
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;
  const [filters, setFilters] = useState<AppFilters>(defaultFilters(30));
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!assetId) return;
    setLoading(true);
    appAnalyticsAPI.getOverview(filterParams(assetId, filters))
      .then(r => setData(r.data?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [assetId, filters]);

  const c = data?.cards || {};
  return (
    <DashboardLayout>
      <AppStyles />
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
          <div>
            <h4 className="fw-bold mb-0"><i className="bi bi-phone text-primary me-2"></i>App analytics</h4>
            <p className="text-muted small mb-0">Installs, active users, sessions, conversions and revenue from your mobile app.</p>
          </div>
        </div>
        <AppNav />
        {data && !data.has_plan ? <NoPlanNotice /> : (
          <>
            <FilterBar assetId={assetId} value={filters} onChange={setFilters} />
            {loading && !data ? (
              <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
            ) : data && (
              <>
                <div className="row g-3 mb-3">
                  <div className="col-6 col-md-4 col-xl-3"><StatCard label="Installs (first opens)" value={fmtNum(c.installs)} icon="bi-download" color="#6366f1" hint={`${fmtNum(c.new_users)} new users`} /></div>
                  <div className="col-6 col-md-4 col-xl-3"><StatCard label="Active users" value={fmtNum(c.active_users)} icon="bi-people" color="#0ea5e9" hint={`DAU ${fmtNum(c.dau)} · WAU ${fmtNum(c.wau)} · MAU ${fmtNum(c.mau)}`} /></div>
                  <div className="col-6 col-md-4 col-xl-3"><StatCard label="Sessions" value={fmtNum(c.sessions)} icon="bi-collection-play" color="#f59e0b" hint={`avg ${fmtDuration(c.avg_session_sec)} per session`} /></div>
                  <div className="col-6 col-md-4 col-xl-3"><StatCard label="Conversion rate" value={`${c.conversion_rate ?? 0}%`} icon="bi-bullseye" color="#10b981" hint={`${fmtNum(c.converted_users)} users converted`} /></div>
                  <div className="col-6 col-md-4 col-xl-3"><StatCard label="Revenue" value={fmtMoney(c.revenue, c.currency)} icon="bi-cash-coin" color="#16a34a" hint={`${fmtNum(c.purchases)} purchases`} /></div>
                  <div className="col-6 col-md-4 col-xl-3"><StatCard label="Events" value={fmtNum(c.events)} icon="bi-lightning-charge" color="#64748b" /></div>
                </div>

                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-body">
                    <h6 className="fw-bold mb-2">Daily active users & installs</h6>
                    {(data.daily || []).length === 0 ? (
                      <div className="text-muted small py-4 text-center">No events in this period. <Link to="/app-analytics/setup">Check the integration →</Link></div>
                    ) : (
                      <div style={{ height: 260 }}>
                        <ResponsiveContainer>
                          <AreaChart data={data.daily} margin={{ left: -10, right: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Area type="monotone" dataKey="active_users" name="Active users" stroke="#6366f1" fill="#6366f133" />
                            <Area type="monotone" dataKey="installs" name="Installs" stroke="#10b981" fill="#10b98133" />
                            <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#f59e0b" fill="#f59e0b22" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-6 col-xl-4"><BreakdownList title="Top screens" icon="bi-window" rows={data.top_screens || []} /></div>
                  <div className="col-md-6 col-xl-4"><BreakdownList title="Top events" icon="bi-lightning" rows={data.top_events || []} /></div>
                  <div className="col-md-6 col-xl-4"><BreakdownList title="App versions" icon="bi-tag" rows={data.app_versions || []} /></div>
                  <div className="col-md-6 col-xl-4"><BreakdownList title="Platforms" icon="bi-phone" rows={data.platforms || []} /></div>
                  <div className="col-md-6 col-xl-4"><BreakdownList title="Countries" icon="bi-globe" rows={data.countries || []} /></div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AppOverviewPage;
